import { MobileScoutingState } from '../mobile/mobileViews/mobileScouting/mobileScoutingState';
import { StringToAnyMap } from '../types/common';
import { PlayerScoutingFiltersState, RatingRequirement, SeasonScoutingFiltersState } from '../types/scouting';
import { AlertsState } from '../views/scouting/alerts/alertsState';


// get the filters to save when saving a search
export const getSearchFiltersToSave = (
  playerScoutingFilters?: PlayerScoutingFiltersState,
  seasonScoutingFilters?: SeasonScoutingFiltersState,
): StringToAnyMap => {

  const search: StringToAnyMap = {};

  // common filters
  const maxAge = playerScoutingFilters?.selectedMaxAge ?? seasonScoutingFilters?.selectedMaxAge ?? [];
  if (maxAge.length > 0) search['maxAge'] = maxAge;

  const positions = playerScoutingFilters?.selectedPositions ?? seasonScoutingFilters?.selectedPositions ?? [];
  if (positions.length > 0) search['positions'] = positions;

  const roles = playerScoutingFilters?.selectedRoles ?? seasonScoutingFilters?.selectedRoles ?? [];
  if (roles.length > 0) search['roles'] = roles;

  // we don't want/need to save all club properties, only the ones relevant to the search and UI
  const clubs = playerScoutingFilters?.selectedClubs ?? seasonScoutingFilters?.selectedClubs ?? [];
  if (clubs.length > 0) search['clubs'] = clubs.map(club => ({ id: club.id, name: club.name, logo_url: club.logo_url }));

  const orderBy = playerScoutingFilters?.selectedOrderBy ?? seasonScoutingFilters?.selectedOrderBy ?? [];
  if (orderBy.length > 0) search['orderBy'] = orderBy;

  const contractStatus = playerScoutingFilters?.selectedContractStatus ?? seasonScoutingFilters?.selectedContractStatus ?? [];
  if (contractStatus.length > 0) search['contractStatus'] = contractStatus;

  const isContractStatusToggled = playerScoutingFilters?.isContractStatusToggled ?? seasonScoutingFilters?.isContractStatusToggled ?? false;
  if (isContractStatusToggled) search['isContractStatusToggled'] = isContractStatusToggled;

  const nationalities = playerScoutingFilters?.selectedNationalities ?? seasonScoutingFilters?.selectedNationalities ?? [];
  if (nationalities.length > 0) search['nationalities'] = nationalities;

  // player scouting filters
  if (playerScoutingFilters && playerScoutingFilters.selectedMinSkillConfidence.length > 0) {
    search['minSkillConfidence'] = playerScoutingFilters.selectedMinSkillConfidence;
  }
  if (playerScoutingFilters && playerScoutingFilters.selectedMinConfidence.length > 0) {
    search['minConfidence'] = playerScoutingFilters.selectedMinConfidence;
  }
  if (playerScoutingFilters && playerScoutingFilters.selectedLeagues.length > 0) {
    search['leagues'] = playerScoutingFilters.selectedLeagues;
  }
  if (playerScoutingFilters && playerScoutingFilters.selectedForm.length > 0) {
    search['form'] = playerScoutingFilters.selectedForm;
  }
  if (playerScoutingFilters && playerScoutingFilters.selectedPlayingTime.length > 0) {
    search['playingTime'] = playerScoutingFilters.selectedPlayingTime;
  }

  // season scouting filters
  if (seasonScoutingFilters && seasonScoutingFilters.selectedMinutes.length > 0) {
    search['minMinutes'] = seasonScoutingFilters.selectedMinutes;
  }
  if (seasonScoutingFilters && seasonScoutingFilters.selectedIterations.length > 0) {
    search['iterations'] = seasonScoutingFilters.selectedIterations;
  }
  if (seasonScoutingFilters && seasonScoutingFilters.selectedRatings.length > 0) {
    search['ratings'] = seasonScoutingFilters.selectedRatings;
  }

  return search;
};


// get the filters to load when loading a search
export const getSearchFiltersStateToLoad = (
  search: StringToAnyMap,
): Partial<PlayerScoutingFiltersState> | Partial<SeasonScoutingFiltersState> => {

  // common filters
  const commonFilters: Partial<PlayerScoutingFiltersState> | Partial<SeasonScoutingFiltersState> = {
    selectedMaxAge: search.maxAge ?? [],
    selectedPositions: search.positions ?? [],
    selectedRoles: search.roles ?? [],
    selectedClubs: search.clubs ?? [],
    selectedContractStatus: search.contractStatus ?? [],
    isContractStatusToggled: search.isContractStatusToggled ?? false,
    selectedNationalities: search.nationalities ?? [],
    selectedOrderByWasExplicitlySet: true,
  };

  if (search.searchType === 'playerScouting') {
    return {
      ...commonFilters,
      selectedMinSkillConfidence: search.minSkillConfidence ?? [],
      selectedMinConfidence: search.minConfidence ?? [],
      selectedLeagues: search.leagues ?? [],
      selectedForm: search.form ?? [],
      selectedPlayingTime: search.playingTime ?? [],
      selectedOrderBy: search.orderBy ?? ['skill_index'],
    } as Partial<PlayerScoutingFiltersState>;
  }

  return {
    ...commonFilters,
    selectedMinutes: search.minMinutes ?? [],
    selectedIterations: search.iterations ?? [],
    selectedRatings: search.ratings ?? [],
    selectedOrderBy: search.orderBy ?? ['skill_rating'],
  } as Partial<SeasonScoutingFiltersState>;
};


// get the filters used for a search
export const getFiltersUsed = (
  queryOptions: StringToAnyMap,
): string[] => {

  const filtersUsed: string[] = [];

  // common filters
  if (queryOptions.maxAge) { filtersUsed.push('maxAge'); }
  if (queryOptions.positionGroup) { filtersUsed.push('roles'); }
  if (queryOptions.primaryPositions) { filtersUsed.push('primaryPositions'); }
  if (queryOptions.countryCodes) { filtersUsed.push('nationalities'); }
  if (queryOptions.maxContractLength) { filtersUsed.push('maxContract'); }

  // player overview and alert filters
  if (queryOptions.minConfidence) { filtersUsed.push('minConfidence'); }
  if (queryOptions.competitions) { filtersUsed.push('leagues'); }
  if (queryOptions.form) { filtersUsed.push('form'); }
  if (queryOptions.playingTime) { filtersUsed.push('playingTime'); }

  // player overview and season scouting filters
  if (queryOptions.clubs) { filtersUsed.push('clubs'); }

  // alert filters
  if (queryOptions.indexRequirements) { filtersUsed.push('indexRequirements'); }
  if (queryOptions.isDebutToggled) { filtersUsed.push('isDebutToggled'); }
  if (queryOptions.logisticTeamIds) { filtersUsed.push('logisticTeams'); }

  // season scouting filters
  if (queryOptions.minMinutesPlayed || queryOptions.minMinutesPlayedPercentage) { filtersUsed.push('minMinutes'); }
  if (queryOptions.iterations) { filtersUsed.push('iterations'); }
  if (queryOptions.ratingRequirements) { filtersUsed.push('ratings'); }

  if (filtersUsed.length === 0) { filtersUsed.push('noFilters'); }

  return filtersUsed;
};


// searching for GB is not allowed, so if GB is selected, we instead select all GB sub-regions
export const getCountryCodeSearchString = (countryCodes: string[]): string => {

  const filteredCountryCodes = new Set<string>();

  countryCodes.forEach((countryCode) => {
    if (countryCode === 'GB') {
      filteredCountryCodes.add('GB');
      filteredCountryCodes.add('GB-ENG');
      filteredCountryCodes.add('GB-NIR');
      filteredCountryCodes.add('GB-SCT');
      filteredCountryCodes.add('GB-WLS');
    } else {
      filteredCountryCodes.add(countryCode);
    }
  });

  return Array.from(filteredCountryCodes).join(',');
};


// get the confidence requirements for a search (the search state and query options differ slightly)
export const getConfidenceRequirements = (
  state: PlayerScoutingFiltersState | MobileScoutingState | AlertsState,
  hasSkillcorner: boolean,
): RatingRequirement[] => {
  const confidenceRequirements: RatingRequirement[] = [];

  if (state.selectedMinSkillConfidence.length > 0) {
    confidenceRequirements.push({ metric: 'skill_index', value: String(parseInt(state.selectedMinSkillConfidence[0]) / 100) });
  }

  if (state.selectedMinConfidence.length > 0) {
    const indexes = ['skill_index', ...state.selectedRoles];

    const isGoalkeeperSearch = state.selectedPositions.includes('GK');
    if (hasSkillcorner && !isGoalkeeperSearch) {
      indexes.push('physical_index');
    }

    indexes.forEach(index => {
      confidenceRequirements.push({ metric: index, value: String(parseInt(state.selectedMinConfidence[0]) / 100) });
    });
  }

  return confidenceRequirements;
};
